import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";

import { BackButton, LoadingIcon } from "@mwi/ui";

import { RootState } from "store";
import { getConversation, markConversationAsRead } from "slices/messages";
import ErrorMessageWithRetry from "../../components/ErrorMessageWithRetry";

import MessageInput from "./MessageInput";
import MessagesHeader from "./MessagesHeader";
import NewConversationHeader from "./NewConversationHeader";
import MessagesContent from "./MessagesContent";

const MessagesSection = ({
  conversationId,
  newConversation,
}: {
  conversationId?: string;
  newConversation?: boolean;
}): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { error, isLoadingConversation, draftConversation, conversations } =
    useSelector((state: RootState) => ({
      error: state.messages.isErrorConversation,
      isLoadingConversation: state.messages.isLoadingConversation,
      draftConversation: state.messages.draftConversation,
      conversations: state.messages.conversations,
    }));

  useEffect(() => {
    if (conversationId) dispatch(getConversation({ conversationId }));
  }, [conversationId]);

  useEffect(() => {
    // Navigate to the new conversation after its created
    if (draftConversation) {
      const matchedConversation = Object.values(conversations).find(
        (conv) => conv.sourceId == draftConversation.sourceId,
      );
      if (matchedConversation) {
        navigate(`/messages/${matchedConversation.id}`);
      }
    }
  }, [draftConversation, conversations]);

  useEffect(() => {
    if (
      conversationId &&
      conversations &&
      conversations[conversationId]?.numUnread
    ) {
      dispatch(markConversationAsRead({ conversationId }));
    }
  }, [conversationId, conversations]);

  const conversation = useMemo(() => {
    if (conversationId && conversations) return conversations[conversationId];
  }, [conversationId, conversations]);

  const getContent = () => {
    if (newConversation)
      return (
        <>
          <NewConversationHeader />
          <div className="h-full overflow-auto relative flex-grow overflow-auto px-4" />
          <MessageInput />
        </>
      );

    if (error)
      return (
        <ErrorMessageWithRetry
          title="Something went wrong"
          message="We couldn't fetch your conversation."
          onRetry={
            conversationId
              ? () => dispatch(getConversation({ conversationId }))
              : undefined
          }
          data-test-id="Messages_Error"
        />
      );

    if (!conversationId)
      return (
        <div className="flex flex-col flex-grow items-center justify-center">
          <p className="p-6" data-test-id="Messages_NoConversationSelected">
            Select a conversation to view messages
          </p>
        </div>
      );

    if (isLoadingConversation)
      return (
        <div
          className="flex flex-col flex-grow items-center justify-center"
          data-test-id="Messages_Loading"
        >
          <LoadingIcon className="w-10 h-10 text-grey" />
        </div>
      );

    if (!conversation)
      return (
        <div className="flex flex-col flex-grow items-center justify-center">
          <p className="p-6" data-test-id="Messages_ConversationNotFound">
            Conversation not found
          </p>
        </div>
      );

    if (conversation)
      return (
        <>
          <MessagesHeader conversation={conversation} />
          <MessagesContent conversation={conversation} />
          <MessageInput conversationId={conversationId} />
        </>
      );
  };

  return (
    <>
      <div
        className={classNames(
          "flex flex-row justify-between px-4 mt-4 tablet:hidden",
          { hidden: conversation || (!conversationId && !newConversation) },
        )}
      >
        <BackButton
          to="/messages"
          variant="grey"
          data-test-id="Messages_BackButton"
        />
      </div>
      <div
        className={classNames(
          "h-full overflow-hidden relative flex flex-col flex-grow tablet:col-start-5 tablet:col-end-13 tablet:bg-white tablet:shadow-default tablet:p-6",
          { "hidden tablet:flex": !conversationId && !newConversation },
        )}
      >
        <div className="h-full overflow-hidden relative flex flex-col flex-grow tablet:border tablet:border-grey-light tablet:bg-grey-lightest">
          {getContent()}
        </div>
      </div>
    </>
  );
};

export default MessagesSection;
